/**
 * Utility to sanitize image URLs by removing m= and q= (quality) parameters
 * and ensuring f=webp format is used.
 */

/**
 * Removes m= and q= parameters from a query string and ensures f=webp is set.
 * @param queryString - The query string to sanitize (e.g., 'w=767&h=767&m=8&q=80')
 * @returns Sanitized query string with f=webp (e.g., 'w=767&h=767&f=webp')
 */
export const sanitizeImageQueryString = (queryString: string): string => {
    if (!queryString) {
        return 'f=webp';
    }

    // Split into individual parameters
    const params = queryString.split('&');

    // Filter out m= and q= parameters, and track if f= exists
    let hasFormatParam = false;
    const filteredParams = params
        .map(param => {
            const [key] = param.split('=');

            // Remove m= (mode) and q= (quality) parameters
            // if (key === 'm' || key === 'q') {
            //     return null;
            // }

            // Replace f=jpg/jpeg/png with f=webp
            if (key === 'f') {
                hasFormatParam = true;
                return 'f=webp';
            }

            return param;
        })
        .filter((param): param is string => param !== null);

    // Add f=webp if no format parameter exists
    if (!hasFormatParam) {
        filteredParams.push('f=webp');
    }

    return filteredParams.join('&');
};

/**
 * Sanitizes a full image URL by processing its query string.
 * @param url - The full image URL to sanitize
 * @returns Sanitized URL with f=webp format
 */
export const sanitizeImageUrl = (url: string): string => {
    if (!url) {
        return url;
    }

    // Handle URLs with query parameters after the image extension
    const queryIndex = url.indexOf('&');
    if (queryIndex === -1) {
        // No query params, just append f=webp
        return `${url}&f=webp`;
    }

    const baseUrl = url.substring(0, queryIndex);
    const queryString = url.substring(queryIndex + 1);
    const sanitizedQuery = sanitizeImageQueryString(queryString);

    return `${baseUrl}&${sanitizedQuery}`;
};

/**
 * Sanitizes IImageSettings by cleaning up the q property in each viewport
 * @param settings - The image settings to sanitize
 * @returns Sanitized image settings with f=webp format
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sanitizeImageSettings = <T extends { viewports?: any }>(settings: T | undefined): T | undefined => {
    if (!settings || !settings.viewports) {
        return settings;
    }

    const sanitizedSettings = { ...settings };
    sanitizedSettings.viewports = { ...settings.viewports };

    for (const viewport of Object.keys(sanitizedSettings.viewports)) {
        const viewportSettings = sanitizedSettings.viewports[viewport];
        if (viewportSettings && viewportSettings.q) {
            sanitizedSettings.viewports[viewport] = {
                ...viewportSettings,
                q: sanitizeImageQueryString(viewportSettings.q)
            };
        }
    }

    return sanitizedSettings;
};

export const getOptimizedImageUrl = (
    imageUrl: string,
    width: number | undefined,
    multiplier: number = 2,
    height?: number
): string => {
    if (!imageUrl || !width) {
        return imageUrl;
    }

    const url = new URL(imageUrl);

    url.searchParams.set('w', (width * multiplier).toString());
    
    if (height) {
        url.searchParams.set('h', (height * multiplier).toString());
    }

    return url.toString();
};
